import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenderService } from '@/lib/api/services/tender'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Tender,
  TenderStats,
  CreateTender,
  UpdateTender,
  TenderFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const tenderKeys = {
  all: ['tenders'] as const,
  lists: () => [...tenderKeys.all, 'list'] as const,
  list: (filters?: TenderFilterParams) => [...tenderKeys.lists(), filters] as const,
  details: () => [...tenderKeys.all, 'detail'] as const,
  detail: (id: string) => [...tenderKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: TenderFilterParams) =>
    [...tenderKeys.all, 'filtered', params, filters] as const,
  search: (query: string) => [...tenderKeys.all, 'search', query] as const,
  stats: () => [...tenderKeys.all, 'stats'] as const
}

// Hook to filter tenders with pagination
export function useFilterTenders(
  params: PaginationParams = { page: 1, size: 10 },
  filters: TenderFilterParams = {},
  enabled?: boolean
) {
  return useQuery<ApiResponse<PaginatedResponse<Tender>>, Error>({
    queryKey: tenderKeys.filtered(params, filters),
    queryFn: () => tenderService.filterTenders(params, filters),
    enabled: enabled
  })
}

// Hook to fetch a single tender
export function useTender(tenderId?: string) {
  return useQuery<ApiResponse<Tender>, Error>({
    queryKey: tenderKeys.detail(tenderId!),
    queryFn: () => tenderService.getTenderById(tenderId!),
    enabled: !!tenderId
  })
}

// Hook to create a tender
export function useCreateTender() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Tender>, Error, CreateTender>({
    mutationFn: (data) => tenderService.createTender(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: tenderKeys.all })
      queryClient.invalidateQueries({ queryKey: tenderKeys.stats() })
    }
  })
}

// Hook to update a tender
export function useUpdateTender() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Tender>,
    Error,
    { tenderId: string; data: UpdateTender }
  >({
    mutationFn: ({ tenderId, data }) =>
      tenderService.updateTender(tenderId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: tenderKeys.all })
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(variables.tenderId) })
      queryClient.invalidateQueries({ queryKey: tenderKeys.stats() })
    }
  })
}

// Hook to delete a tender
export function useDeleteTender() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { tenderId: string }
  >({
    mutationFn: ({ tenderId }) =>
      tenderService.deleteTender(tenderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: tenderKeys.all })
      queryClient.invalidateQueries({ queryKey: tenderKeys.stats() })
    }
  })
}

// Hook to fetch draft tenders count
export function useDraftTendersCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...tenderKeys.stats(), 'draft'],
    queryFn: () => tenderService.getDraftTendersCount()
  })
}

// Hook to fetch published tenders count
export function usePublishedTendersCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...tenderKeys.stats(), 'published'],
    queryFn: () => tenderService.getPublishedTendersCount()
  })
}

// Hook to search tenders
export function useSearchTenders(
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<Tender[]>, Error>({
    queryKey: tenderKeys.search(searchBy || ''),
    queryFn: () => tenderService.searchTenders(searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to fetch bid types
export function useBidTypes() {
  return useQuery<ApiResponse<Array<{ value: string; displayName: string }>>, Error>({
    queryKey: [...tenderKeys.all, 'bidTypes'],
    queryFn: () => tenderService.getBidTypes(),
    staleTime: 30 * 60 * 1000 // 30 minutes - bid types don't change often
  })
}

// Hook to fetch tender categories
export function useTenderCategories() {
  return useQuery<ApiResponse<Array<{ value: string; displayName: string }>>, Error>({
    queryKey: [...tenderKeys.all, 'tenderCategories'],
    queryFn: () => tenderService.getTenderCategories(),
    staleTime: 30 * 60 * 1000
  })
}

// Hook to fetch tender stats
export function useTenderStats() {
  return useQuery<TenderStats, Error>({
    queryKey: tenderKeys.stats(),
    queryFn: async () => {
      const response = await tenderService.getTenderStats()
      return response.data
    },
    staleTime: 5 * 60 * 1000
  })
}
